/* STATUS / AGENT NOTES (2026-10-03)
 * Agent: Single collection banners retain their stylesheet-painted pose when the decoded runtime takes over.
 * Agent: Collection scroll chevrons share the existing visibility, focus and pause controls.
 * Agent START: Codex alpha-video selection, cached slide decoding and continuous camera drift; Moneymate demonstration at 2x.
 * Agent: Each connection waits for its own complete-markup sentinel and the shared stylesheet before one guarded initialization.
 * Agent: Navigation preserves explicit Pause; otherwise dots, arrows and swipes keep autoplay, with directional scene wipes.
 * Agent: Single category photos decode into view before the three hidden case renders begin their cascade.
 * Agent: A category scene already revealed by the critical CSS failsafe is marked ready without replaying its entrance.
 */
(() => {
  if (customElements.get('scooch-fall-motion')) return;
  const cycleDuration = 7000;
  const transitionDuration = 620;
  customElements.define('scooch-fall-motion', class extends HTMLElement {
    connectedCallback() {
      // Agent START: Component-scoped streamed-markup and stylesheet readiness gate.
      const connection = (this.connection || 0) + 1;
      this.connection = connection;
      this.abort?.abort();
      this.contentObserver?.disconnect();
      this.contentObserver = null;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.initialized = false;
      // Agent START: Reconnected category markup must not expose the previous entrance's ready state.
      if (this.hasAttribute('data-category')) {
        this.removeAttribute('data-ready');
        this.removeAttribute('data-background-ready');
      }
      // Agent END: Clean first-paint gate on every connection.
      const adjacentStylesheet = this.previousElementSibling?.matches('[data-scooch-fall-motion-styles]') ? this.previousElementSibling : null;
      const stylesheet = adjacentStylesheet || document.querySelector('[data-scooch-fall-motion-styles]') || document.querySelector('link[href*="scooch-fall-motion.css"]');
      const isCurrent = () => this.isConnected && !signal.aborted && this.connection === connection && !this.initialized;
      const revealStatic = () => {
        if (!isCurrent()) return;
        this.initialized = true;
        this.contentObserver?.disconnect();
        this.contentObserver = null;
        this.setAttribute('data-ready', '');
        this.setAttribute('data-motion-static', '');
        this.querySelector('.scooch-fall-motion__navigation')?.setAttribute('hidden', '');
      };
      const initializeWhenReady = () => {
        if (!isCurrent()) return;
        const contentReady = !!this.querySelector('[data-scooch-fall-motion-end]') || document.readyState !== 'loading';
        if (!contentReady) return;
        if (!stylesheet) {
          revealStatic();
          return;
        }
        if (stylesheet.dataset.scoochFallMotionStyles === 'error') {
          revealStatic();
          return;
        }
        if (!stylesheet.sheet) {
          if (document.readyState === 'complete') revealStatic();
          return;
        }
        if (!this.querySelector('[data-slide]')) {
          revealStatic();
          return;
        }
        this.initialized = true;
        this.contentObserver?.disconnect();
        this.contentObserver = null;
        this.initialize(signal);
      };
      if (!this.querySelector('[data-scooch-fall-motion-end]') && document.readyState === 'loading') {
        this.contentObserver = new MutationObserver(initializeWhenReady);
        this.contentObserver.observe(this, { childList:true });
      }
      if (stylesheet && !stylesheet.sheet && stylesheet.dataset.scoochFallMotionStyles !== 'error') {
        stylesheet.addEventListener('load', initializeWhenReady, { once:true, signal });
        stylesheet.addEventListener('error', revealStatic, { once:true, signal });
      }
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeWhenReady, { once:true, signal });
      if (document.readyState !== 'complete') window.addEventListener('load', initializeWhenReady, { once:true, signal });
      initializeWhenReady();
      // Agent END: Guarded component startup.
    }
    initialize(signal) {
      if (this.hasAttribute('data-motion-static')) {
        this.removeAttribute('data-motion-static');
        this.removeAttribute('data-ready');
      }
      this.slides = [...this.querySelectorAll('[data-slide]')];
      // Agent: Slide markup is immutable for this connection; release caches on reconnect.
      this.decodedSlides = new WeakMap();
      this.videos = this.slides.map(slide => slide.querySelector('[data-motion-video]'));
      // Agent START: Codex poster-first source gate and compositor-ready handoff.
      this.videoTimers = new Map();
      this.videos.filter(Boolean).forEach(video => {
        video.addEventListener('playing', () => {
          const reveal = () => {
            if (signal.aborted || video.dataset.failed) return;
            if (video !== this.videos[this.index] || this.loading || !this.visible || this.paused || document.hidden || this.motion.matches) { video.pause(); return; }
            video.parentElement.setAttribute('data-video-ready', '');
          };
          if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(reveal);
          else requestAnimationFrame(reveal);
        }, { signal });
      });
      // Agent END: Codex poster handoff.
      this.videos.filter(Boolean).forEach(video => video.addEventListener('error', () => {
        video.dataset.failed = 'true';
        video.parentElement.removeAttribute('data-video-ready');
        video.removeAttribute('src');
        video.load();
      }, { signal }));
      this.single = this.slides.length === 1;
      this.toggleAttribute('data-single', this.single);
      this.dots = [...this.querySelectorAll('[data-slide-index]')];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.generation = (this.generation || 0) + 1;
      this.started = false;
      this.parallaxFrame = null;
      this.animations = [];
      this.manualEntrance = false;
      this.paused = this.motion.matches;
      this.manualPaused = false;
      this.visible = false;
      this.replay = this.querySelector('[data-fall-replay]');
      this.pauseButton = this.querySelector('[data-slide-pause]');
      const navigation = this.querySelector('.scooch-fall-motion__navigation');
      this.hasVideo = !!this.querySelector('[data-motion-video]');
      // Agent START 2026-10-02: Collection cue shares the existing animation pause control.
      this.hasScrollCue = !!this.querySelector('.scooch-collection-scroll-cue');
      if (navigation) navigation.hidden = this.single && !this.hasVideo && !this.hasScrollCue;
      // Agent END: Homepage navigation rules remain unchanged.
      this.replay && (this.replay.hidden = this.motion.matches);
      this.updatePauseButton();
      this.pauseButton?.addEventListener('click', () => {
        this.manualPaused = !this.manualPaused;
        this.paused = this.manualPaused || this.motion.matches;
        // Explicit Play resumes while its own control remains focused.
        if (!this.manualPaused) this.manualEntrance = false;
        this.updatePauseButton();
        this.schedule();
      }, { signal });
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.select(index, true), { signal }));
      this.replay?.addEventListener('click', () => this.select(this.index), { signal });
      // Agent START 2026-10-02: Codex keeps carousel navigation playing while preserving single-header focus holds.
      this.addEventListener('focusin', () => {
        if (!this.single) return;
        this.finish();
        this.paused = true;
        // Agent START 2026-10-02: Keyboard focus also holds the collection cue.
        this.syncScrollCue();
        // Agent END: Reuse the scoped focus handler.
        this.stopTimer(true);
        this.syncVideos();
      }, { signal });
      this.addEventListener('focusout', event => {
        if (!this.single || this.contains(event.relatedTarget)) return;
        // Agent START: Leaving a control must not restart an already playing timer or camera.
        const wasPaused = this.paused;
        this.paused = this.manualPaused || this.motion.matches;
        if (this.paused !== wasPaused) this.schedule();
        // Agent END: Stable focus handoff.
      }, { signal });
      // Agent END: Carousel focus does not set a pause; the explicit button owns manual holds.
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', () => {
        this.finish();
        if (this.motion.matches) this.clearBackgroundMotion();
        this.requestParallax();
        // Agent: Codex 2026-10-02 keeps carousel focus out of the reduced-motion pause calculation.
        this.paused = this.manualPaused || this.motion.matches || (this.single && this.contains(document.activeElement));
        this.replay && (this.replay.hidden = this.motion.matches);
        this.updatePauseButton();
        this.schedule();
      }, { signal });
      this.addEventListener('keydown', event => {
        if (this.single || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
      }, { signal });
      const viewport = this.querySelector('.scooch-fall-motion__viewport');
      viewport.addEventListener('pointerdown', event => {
        if (this.single || event.target.closest('button') || event.pointerType === 'mouse') return;
        this.touch = { x: event.clientX, y: event.clientY };
      }, { signal });
      viewport.addEventListener('pointercancel', () => { this.touch = null; }, { signal });
      viewport.addEventListener('pointerup', event => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch.x, dy = event.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        this.suppressClickUntil = performance.now() + 400;
        // Agent START 2026-10-02: Codex sweeps the new scene in the direction of the horizontal gesture.
        this.select(this.index + (dx < 0 ? 1 : -1), true, dx < 0 ? -1 : 1);
        // Agent END: Direction remains correct when the slide index wraps.
      }, { signal });
      viewport.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) event.preventDefault();
      }, { signal, capture:true });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.requestParallax();
        if (this.visible && !this.started) {
          this.started = true;
          this.select(0);
        } else this.schedule();
      }, { threshold:.25 });
      this.observer.observe(viewport);
      // Agent START: Background-only parallax; no idle animation loop or product movement.
      this.parallaxLayers = [...this.querySelectorAll('[data-motion-parallax]')];
      if (this.hasAttribute('data-parallax') && this.parallaxLayers.length) {
        window.addEventListener('scroll', () => this.requestParallax(), { passive:true, signal });
        window.addEventListener('resize', () => this.requestParallax(), { passive:true, signal });
      }
      this.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('[data-slide]');
        if (!slide) return;
        this.manualPaused = true;
        this.paused = true;
        this.updatePauseButton();
        this.select(this.slides.indexOf(slide), true);
      }, { signal });
      // Agent END: Background parallax and editor selection.
    }
    requestParallax() {
      if (!this.hasAttribute('data-parallax') || !this.parallaxLayers?.length || this.parallaxFrame) return;
      if (!this.visible && !this.motion.matches) return;
      this.parallaxFrame = requestAnimationFrame(() => {
        this.parallaxFrame = null;
        const offset = this.motion.matches ? 0 : Math.round(Math.max(-24, Math.min(24, -this.getBoundingClientRect().top * .07)));
        if (offset === this.parallaxOffset) return;
        this.parallaxOffset = offset;
        this.parallaxLayers.forEach(layer => layer.style.transform = `translate3d(0,${offset}px,0)`);
      });
    }
    load(slide) {
      if (this.decodedSlides.has(slide)) return this.decodedSlides.get(slide);
      slide.querySelectorAll('[data-srcset]').forEach(node => { node.srcset = node.dataset.srcset; delete node.dataset.srcset; });
      slide.querySelectorAll('[data-src]').forEach(node => { node.src = node.dataset.src; delete node.dataset.src; });
      // Agent START: Decode the category photograph first, then reveal it while case/title images finish decoding.
      const images = [...slide.querySelectorAll('img')];
      const background = slide.querySelector('[data-fall-layer="background"] img');
      const backgroundDecoded = background ? background.decode().catch(() => {}) : Promise.resolve();
      // Agent: Bind the background gate to this connection so a stale decode cannot open a reconnected hero.
      const { signal } = this.abort;
      if (this.hasAttribute('data-category') && slide === this.slides[0]) backgroundDecoded.then(() => {
        if (this.isConnected && !signal.aborted) this.setAttribute('data-background-ready', '');
      });
      const decoded = Promise.all(images.map(img => img === background ? backgroundDecoded : img.decode().catch(() => {})));
      // Agent END: Background-first image decoding without delaying other downloads.
      this.decodedSlides.set(slide, decoded);
      return decoded;
    }
    // Agent START: Preserve the exact progress frame during every temporary/manual hold.
    stopTimer(hold = false, keepCamera = false) {
      clearTimeout(this.timer);
      this.timer = null;
      if (!keepCamera) [this.backgroundMotion, this.outgoingBackgroundMotion].forEach(animation => {
        if (!animation) return;
        const time = animation.currentTime;
        animation.pause();
        if (time !== null) animation.currentTime = time;
      });
      if (hold && this.progress) {
        const time = this.progress.currentTime;
        this.progress.pause();
        // Agent: Resolve WAAPI's pending pause without advancing another compositor frame.
        if (time !== null) this.progress.currentTime = time;
      }
      else if (hold) return;
      else {
        this.progress?.cancel();
        this.progress = null;
      }
    }
    syncVideos() {
      this.videos.forEach((video, index) => {
        if (!video) return;
        const hold = index !== this.index || this.loading || !this.visible || this.paused || document.hidden || this.motion.matches;
        if (hold) video.pause();
        else {
          // Agent: Native looping avoids timers; existing visibility and pause gates still own playback.
          video.loop = true;
          // Agent START: Codex Moneymate's 15fps export plays at an effective 30fps without another download.
          const playbackRate = this.slides[index].dataset.family === 'moneymate' ? 2 : 1;
          if (video.defaultPlaybackRate !== playbackRate) video.defaultPlaybackRate = playbackRate;
          if (video.playbackRate !== playbackRate) video.playbackRate = playbackRate;
          // Agent END: Family-specific demonstration timing.
          if (video.dataset.failed) return;
          if (!video.getAttribute('src')) {
            // Agent START: Codex let the poster/entrance paint before requesting video bytes.
            if (!video.dataset.startReady) {
              if (!this.videoTimers.has(video)) this.videoTimers.set(video, setTimeout(() => {
                this.videoTimers.delete(video);
                video.dataset.startReady = 'true';
                if (this.isConnected && !this.abort.signal.aborted) this.syncVideos();
              }, 1200));
              return;
            }
            video.muted = true;
            video.defaultMuted = true;
            // Agent END: Codex poster-first gate.
            // Agent: WebKit needs HEVC alpha; Chromium's HEVC support does not imply alpha support.
            const webkit = /Apple/.test(navigator.vendor) || /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            const type = webkit ? 'video/mp4; codecs="hvc1"' : 'video/webm; codecs="vp9"';
            if (!video.canPlayType(type)) return;
            video.src = webkit && video.dataset.videoHevc ? video.dataset.videoHevc : video.dataset.videoSrc;
          }
          video.play().catch(() => {
            // Agent: Keep the poster and expose the existing explicit Play control.
            if (this.abort.signal.aborted || video !== this.videos[this.index] || this.loading || !this.visible || document.hidden || this.paused) return;
            this.manualPaused = true;
            this.paused = true;
            this.updatePauseButton();
          });
        }
      });
    }
    // Agent END: Frozen progress and bounded product video playback.
    // Agent START: One compositor zoom for the active campaign photograph; no frame loop.
    clearBackgroundMotion(preserve = false) {
      if (!this.backgroundMotion) return;
      const image = this.backgroundMotion.effect.target;
      image.style.transform = preserve ? getComputedStyle(image).transform : '';
      this.backgroundMotion.cancel();
      this.backgroundMotion = null;
    }
    startBackgroundMotion() {
      const image = this.slides[this.index].querySelector('[data-fall-layer="background"] img');
      if (!image || this.single || this.motion.matches) return;
      const current = new DOMMatrix(getComputedStyle(image).transform);
      const width = image.clientWidth;
      this.clearBackgroundMotion();
      const start = Math.max(1.07, current.a), distance = Math.max(0, 1.14 - start);
      const startX = current.a > 1 ? current.m41 : width * .02;
      const travel = -width * .03 - startX;
      const frame = fraction => `translateX(${startX + travel * fraction}px) scale(${start + distance * fraction})`;
      this.backgroundMotion = image.animate([
        { transform:frame(0), offset:0, easing:'cubic-bezier(.15,.65,.35,1)' },
        { transform:frame(.42), offset:.14, easing:'linear' },
        { transform:frame(.64), offset:.78, easing:'cubic-bezier(.65,0,.85,.35)' },
        { transform:frame(1), offset:1 }
      ], { duration:cycleDuration + transitionDuration, fill:'both', easing:'linear' });
    }
    // Agent START: A selected slide gets a short camera entrance, then remains still behind the Play control.
    startManualBackgroundMotion() {
      const image = this.slides[this.index].querySelector('[data-fall-layer="background"] img');
      if (!image || this.single || this.motion.matches || !this.visible || document.hidden) return;
      const width = image.clientWidth;
      this.clearBackgroundMotion();
      this.backgroundMotion = image.animate([
        { transform:`translateX(${width * .02}px) scale(1.07)` },
        { transform:'translateX(0px) scale(1.09)' }
      ], { duration:2200, fill:'both', easing:'cubic-bezier(.23,1,.32,1)' });
    }
    // Agent END: Manual entrance camera.
    // Agent END: Bounded background camera move.
    updatePauseButton() {
      if (!this.pauseButton) return;
      // Agent START 2026-10-02: Keep the native pause control available for animated collection cues.
      this.pauseButton.hidden = (this.single && !this.hasVideo && !this.hasScrollCue) || this.motion.matches;
      // Agent END: Reduced motion keeps the control hidden.
      this.pauseButton.querySelector('[data-pause-icon]').toggleAttribute('hidden', this.manualPaused);
      this.pauseButton.querySelector('[data-play-icon]').toggleAttribute('hidden', !this.manualPaused);
      this.pauseButton.setAttribute('aria-label', this.single ? (this.manualPaused ? 'Play animation' : 'Pause animation') : (this.manualPaused ? 'Play slideshow' : 'Pause slideshow'));
    }
    // Agent START 2026-10-02: Cue follows existing hero state without extra observers or listeners.
    syncScrollCue() {
      if (!this.hasScrollCue) return;
      const paused = !this.visible || this.paused || document.hidden || this.motion.matches;
      if (this.hasAttribute('data-scroll-cue-paused') !== paused) this.toggleAttribute('data-scroll-cue-paused', paused);
    }
    // Agent END: Only update the collection component when its pause state changes.
    schedule() {
      // Agent START 2026-10-02: Existing scheduler also synchronizes the cue.
      this.syncScrollCue();
      // Agent END: Reuse visibility and pause scheduling.
      this.syncVideos();
      if (this.single || !this.isConnected || this.loading || !this.visible || this.paused || document.hidden || this.motion.matches) {
        // Agent START: Finish a shopper-selected scene's transition; an explicit Pause still freezes the camera.
        const keepEntranceCamera = this.manualEntrance && this.visible && !document.hidden && !this.motion.matches;
        this.stopTimer(true, keepEntranceCamera);
        // Agent END: Manual entrance does not restart auto-advance.
        return;
      }
      this.stopTimer(false, true);
      this.outgoingBackgroundMotion?.play();
      this.progress = this.dots[this.index].firstElementChild.animate([
        { transform:'scaleX(0)' }, { transform:'scaleX(1)' }
      ], { duration:cycleDuration, easing:'linear' });
      this.startBackgroundMotion();
      // Agent START: A queued timer cannot undo a manual pause near the cycle boundary.
      this.timer = setTimeout(() => {
        this.timer = null;
        if (!this.manualPaused && !this.paused && !this.loading && this.visible && !document.hidden) this.select(this.index + 1);
      }, cycleDuration);
      // Agent END: Guarded automatic advancement.
    }
    async select(index, manual = false, direction = 0) {
      // Agent START 2026-10-02: Codex preserves explicit Pause when navigating; otherwise selection keeps autoplay.
      if (manual && !this.single) this.paused = this.manualPaused || this.motion.matches;
      const sweepDirection = direction || (manual && index > this.index ? -1 : 1);
      // Agent END: Automatic startup keeps its existing sweep; manual choices follow the selected direction.
      const generation = ++this.generation;
      this.loading = true;
      // Agent: Keep the current photograph moving while the incoming assets decode.
      this.stopTimer(false, (manual || !this.paused) && this.visible && !document.hidden && !this.motion.matches);
      // Agent START: A control-focused camera can finish its outgoing sweep without rewinding a completed pan.
      const camera = this.backgroundMotion;
      if (manual && this.visible && !document.hidden && !this.motion.matches && camera?.playState === 'paused' && camera.currentTime < camera.effect.getComputedTiming().endTime) camera.play();
      // Agent END: Manual outgoing camera handoff.
      this.syncVideos();
      this.finish();
      const targetIndex = (index + this.slides.length) % this.slides.length;
      const slide = this.slides[targetIndex];
      await this.load(slide);
      if (generation !== this.generation || this.abort.signal.aborted) return;
      this.outgoingBackgroundMotion = this.backgroundMotion;
      this.backgroundMotion = null;
      // Agent: Decoding or a late timer can consume the camera's transition allowance.
      // Extend from its exact rendered pose instead of freezing or rewinding the crop.
      if (this.outgoingBackgroundMotion && this.visible && !document.hidden && !this.motion.matches && (manual || !this.paused)) {
        const camera = this.outgoingBackgroundMotion;
        if (camera.effect.getTiming().duration - camera.currentTime < transitionDuration) {
          const image = camera.effect.target, matrix = new DOMMatrix(getComputedStyle(image).transform);
          const from = `translateX(${matrix.m41}px) scale(${matrix.a})`;
          camera.cancel();
          this.outgoingBackgroundMotion = image.animate([
            { transform:from },
            { transform:`translateX(${matrix.m41 - image.clientWidth * .01}px) scale(${matrix.a + .014})` }
          ], { duration:transitionDuration + 40, fill:'both', easing:'linear' });
        }
      }
      const backgroundImage = slide.querySelector('[data-fall-layer="background"] img');
      if (backgroundImage) backgroundImage.style.transform = '';
      const previous = this.slides[this.index];
      this.index = targetIndex;
      const video = slide.querySelector('[data-motion-video]');
      if (video?.getAttribute('src')) video.currentTime = 0;
      this.slides.forEach(item => { item.hidden = item !== slide; });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      if (manual) this.querySelector('.scooch-fall-motion__status').textContent = slide.getAttribute('aria-label');
      this.loading = false;
      // Agent START 2026-10-03: A stylesheet-painted single collection stays visible during the decoded handoff.
      const copy = slide.querySelector('.scooch-fall-motion__copy');
      const exposed = this.single && this.classList.contains('scooch-fall-motion--collection') && !!copy && getComputedStyle(copy).visibility === 'visible';
      // Agent: Codex 2026-10-02 carries the navigation direction through the decoded slide handoff.
      this.play(previous !== slide ? previous : null, manual, exposed, sweepDirection);
      // Agent END: Single collection still reveal.
      // Agent: Reveal only after decoding and installing the entrance's first frame.
      this.setAttribute('data-ready', '');
      if (!this.single) this.load(this.slides[(this.index + 1) % this.slides.length]);
    }
    finish() {
      this.animations?.forEach(animation => animation.cancel());
      this.animations = [];
      this.manualEntrance = false;
      this.clearOutgoing();
    }
    clearOutgoing() {
      if (this.outgoingBackgroundMotion) {
        this.outgoingBackgroundMotion.cancel();
        this.outgoingBackgroundMotion = null;
      }
      if (this.outgoing) {
        this.outgoing.hidden = true;
        this.outgoing.inert = false;
        this.outgoing.removeAttribute('aria-hidden');
        this.outgoing.style.zIndex = '';
        this.outgoing = null;
      }
    }
    // Agent: Codex 2026-10-02 accepts a scene sweep direction without changing the case/title entrance.
    play(previous = null, manual = false, still = false, direction = 1) {
      // Agent: select already finished the old entrance; retain its camera until the wipe completes.
      if (!previous) this.finish();
      const slide = this.slides[this.index];
      // Agent: `still` skips only the entrance for an already visible category scene.
      if (!still && !this.motion.matches && Element.prototype.animate) {
        if (previous) {
          this.outgoing = previous;
          previous.hidden = false;
          previous.inert = true;
          previous.setAttribute('aria-hidden', 'true');
          previous.style.zIndex = '0';
        }
        slide.style.zIndex = '1';
        const animate = (node, frames, options) => {
          const animation = node.animate(frames, { fill:'backwards', easing:'cubic-bezier(.23,1,.32,1)', ...options });
          this.animations.push(animation);
          animation.finished.then(() => {
            animation.cancel();
            this.animations = this.animations.filter(item => item !== animation);
            if (node === slide && this.outgoing === previous) this.clearOutgoing();
            // Agent START: The short camera entrance settles into the selected slide's paused state.
            if (!this.animations.length && this.manualEntrance) {
              this.manualEntrance = false;
              this.schedule();
            }
            // Agent END: Manual entrance completion.
          }).catch(() => {});
        };
        // Agent START 2026-10-02: Codex mirrors the scene reveal for leftward versus rightward navigation.
        if (!this.single) {
        animate(slide, [
          { clipPath:direction < 0 ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)' }, { clipPath:'inset(0 0 0 0)' }
        ], { duration:transitionDuration });
        }
        // Agent END: Single collection headers retain their existing entrance.
        // Agent START: Give a decoded category photograph one clear beat before the existing quick case cascade.
        const backgroundLead = this.single && this.hasAttribute('data-category') ? 120 : 0;
        slide.querySelectorAll('[data-fall-layer="case"]').forEach((node, i) => animate(node, [
          { transform:'translateY(100px)', opacity:0 }, { transform:'translateY(0)', opacity:1 }
        ], { duration:620, delay:(this.single ? backgroundLead : 280) + i * 80 }));
        // Agent END: Background-first case timing.
        // Agent START: Glyph reveal with a finite sheen; controls rise independently.
        const copyDelay = this.single ? 160 + backgroundLead : 520;
        const title = slide.querySelector('.scooch-fall-motion__title');
        animate(title, [
          { clipPath:'inset(-20% 100% -20% -4%)' }, { clipPath:'inset(-20% -4% -20% -4%)' }
        ], { duration:720, delay:copyDelay, easing:'cubic-bezier(.2,.65,.35,1)' });
        const sheen = title.querySelector('[data-title-sheen]');
        if (sheen) animate(sheen, [
          { maskPosition:'100% 50%', webkitMaskPosition:'100% 50%', opacity:1 },
          { maskPosition:'0% 50%', webkitMaskPosition:'0% 50%', opacity:1 }
        ], { duration:1100, delay:copyDelay+720, easing:'linear' });
        slide.querySelectorAll('.scooch-motion-family__description, .scooch-fall-motion__description, .scooch-fall-motion__cta').forEach(node => animate(node, [
          { transform:'translateY(24px)', opacity:0 }, { transform:'translateY(0)', opacity:1 }
        ], { duration:520, delay:copyDelay+160 }));
        // Agent END: Title sheen and independent copy controls.
      }
      // Agent START: Entering a chosen slide stays animated even though its carousel timer is paused.
      this.manualEntrance = manual && this.manualPaused && !this.single && !this.motion.matches;
      this.schedule();
      if (this.manualEntrance) this.startManualBackgroundMotion();
      // Agent END: Manual scene motion without autoplay.
    }
    disconnectedCallback() {
      this.connection = (this.connection || 0) + 1;
      this.generation++;
      this.abort?.abort();
      this.videoTimers?.forEach(timer => clearTimeout(timer));
      this.videoTimers?.clear();
      this.contentObserver?.disconnect();
      this.contentObserver = null;
      this.initialized = false;
      this.observer?.disconnect();
      cancelAnimationFrame(this.parallaxFrame);
      this.parallaxFrame = null;
      this.stopTimer();
      this.clearBackgroundMotion();
      this.slides?.forEach(slide => slide.querySelector('[data-motion-video]')?.pause());
      this.finish();
    }
  });
})();
/* Agent END: Scoped campaign carousel. */
